/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { widgetsUpdate } from "../funcs/widgetsUpdate.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Widget } from "../models/components/widget.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateWidgetRequest,
  UpdateWidgetSecurity,
} from "../models/operations/updatewidget.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateWidgetMutationVariables = {
  request: UpdateWidgetRequest;
  security?: UpdateWidgetSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateWidgetMutationData = Widget;

export type UpdateWidgetMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateWidget widgets
 *
 * @remarks
 * Replace a widget's name, description, dataset, query and visualization. Its creator can; editing someone else's needs project write access.
 */
export function useUpdateWidgetMutation(
  options?: MutationHookOptions<
    UpdateWidgetMutationData,
    UpdateWidgetMutationError,
    UpdateWidgetMutationVariables
  >,
): UseMutationResult<
  UpdateWidgetMutationData,
  UpdateWidgetMutationError,
  UpdateWidgetMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateWidgetMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateWidget(): MutationKey {
  return ["@gram/client", "widgets", "update"];
}

export function buildUpdateWidgetMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateWidgetMutationVariables,
  ) => Promise<UpdateWidgetMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateWidget(),
    mutationFn: function updateWidgetMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateWidgetMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(widgetsUpdate(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
